import './styles/SystemNotificationsList.scss';

import { useQuery } from '@apollo/client';
import { Create } from '@mui/icons-material';
import { Button, Grid, Icon, Paper } from '@mui/material';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import QUERY from 'gql/queries/MessagingService/getSystemNotifications.gql';

import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { buildListHeading, RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { SystemNotificationItem } from 'app/components/RichList/items/Messaging/SystemNotificationItem';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { SystemNotificationModal } from '../SystemNotification';

export function SystemNotificationsList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const systemNotificationsQuery = useQuery<
    any,
    {
      full?: boolean;
    }
  >(QUERY, {
    variables: { full: true }
  });
  const { refetch } = systemNotificationsQuery;

  usePageHeading({
    heading: t(translations.layout.menu.settingsSystemNotifications()),
    mobilePadding: false
  });

  const ControlButtons = () => {
    return (
      <>
        <Button
          startIcon={<Create />}
          variant="contained"
          className="system-notifications-management__actions--primary"
          onClick={() => {
            setCreateModalOpen(true);
          }}
        >
          {t(translations.common.create())}
        </Button>
      </>
    );
  };

  const filters = [];
  const getListHeading = () => {
    return buildListHeading(
      [
        { weight: 1, title: t(translations.common.id()) },
        { weight: 2, title: t(translations.pages.systemNotificationsSettings.type()) },
        { weight: 6, title: t(translations.pages.systemNotificationsSettings.notification()) },
        { weight: 2, title: t(translations.pages.systemNotificationsSettings.timePeriod()) },
        { weight: 1, title: t(translations.pages.shibUserList.actions()) }
      ],
      'system-notifications-management__list-heading__item'
    );
  };

  return (
    <GQLGuard
      query={systemNotificationsQuery}
      onError={error => {
        dispatch(
          AlertActions.error(
            `${t(
              translations.pages.systemNotificationsSettings.alerts.error.loadSystemNotifications()
            )}`
          )
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {systemNotificationsData => {
        return (
          <>
            <ErrorBoundary boundaryTag="systemNotificationManagementBoundary">
              <Modal
                open={createModalOpen}
                onClose={(props, reason) => {
                  if (reason && reason === 'backdropClick') return;
                  setCreateModalOpen(false);
                }}
                headline={t(translations.pages.systemNotificationsSettings.newSystemNotification())}
                onCancel={() => {
                  setCreateModalOpen(false);
                }}
                disabledCancel={disabledCancel}
              >
                <SystemNotificationModal
                  closeHandler={() => setCreateModalOpen(false)}
                  setDisabledCancel={setDisabledCancel}
                />
              </Modal>
            </ErrorBoundary>
            <Grid
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                paddingBottom: '1em'
              }}
              container
            >
              <ControlButtons />
            </Grid>
            <Grid container className="system-notifications-management" component={Paper}>
              <RichList
                filters={filters}
                onFilterChange={refetch}
                staticHeading={
                  <Grid
                    container
                    className="system-notifications-management__list-heading"
                    component={Paper}
                    square
                  >
                    {getListHeading()}
                  </Grid>
                }
              >
                {systemNotificationsData.systemNotifications.length === 0 ? (
                  <NoItems icon={<Icon className="far fa-smile-beam" />} />
                ) : (
                  <Grid container component={Paper} square>
                    {systemNotificationsData.systemNotifications.map(notification => (
                      <SystemNotificationItem notification={notification} />
                    ))}
                  </Grid>
                )}
              </RichList>
            </Grid>
          </>
        );
      }}
    </GQLGuard>
  );
}
